import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Popper,
  Select,
  TableHead,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { visuallyHidden } from "@mui/utils";
import {
  StyledTableCell as TableCell,
  StyledTableRow as TableRow,
} from "../../libs/ui/StyledComponents";
import { RequestTableHeadCell } from "./type";
import {
  BooleanFilterOperators,
  DateFilterOperators,
  Order,
  StringFilterOperators,
} from "../../const/type";

interface ResourceTableProps {
  headCells: RequestTableHeadCell[];
  numSelected: number;
  rowCount: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order?: Order;
  orderBy?: string;
  onRequestSort?: (property: string) => void;
  filters: Record<string, Record<string, string | Date | boolean>>;
  setFilters: (
    value: Record<string, Record<string, string | Date | boolean>>
  ) => void;
}

export function RequestTableHead(props: ResourceTableProps) {
  const {
    headCells,
    numSelected,
    rowCount,
    onSelectAllClick,
    orderBy,
    order = "desc",
    onRequestSort,
    filters,
    setFilters,
  } = props;

  const [tempFilterId, setTempFilterId] = useState("");
  const [tempFilterType, setTempFilterType] = useState("equalTo");
  const [tempFilterText, setTempFilterText] = useState<string | Date | boolean>(
    ""
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // const handleClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
  //   setAnchorEl(anchorEl ? null : event.currentTarget);
  //   setFilterId(id);
  // };

  const handleClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
    const existingFilter = filters[id];
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setTempFilterId(id);
 
    if (existingFilter) {
      const [operator] = Object.keys(existingFilter);
      setTempFilterType(operator);
      setTempFilterText(existingFilter[operator]);
    } else {
      if (["ministryCategory", "requestStatus"].includes(id)) { 
        setTempFilterType("iLike");
        setTempFilterText("");
      } else if (
        // Set default operator and value
        ["status", "organization", "firstName", "lastName", "email", "city", "state"].includes(id)
      ) {
          setTempFilterType("iLike");
          setTempFilterText("");
        } else if (["requestTime", "shippedTime"].includes(id)) {
          setTempFilterType("lessThanEqual");
          setTempFilterText("");
        } else if (["redFlag", "yellowFlag"].includes(id)) {
          setTempFilterType("equalTo");
          setTempFilterText(false);
      }
    }
  };
  

  const handleApplyFilter = () => {
    if (!tempFilterId || tempFilterText === "") return;
    const newFilters = {
      ...filters,
      [tempFilterId]: {
        [tempFilterType]: tempFilterText,
      },
    };
    setFilters(newFilters);
    setAnchorEl(null);
  };

  const handleClearFilter = (id: string) => {
    const updatedFilters = { ...filters };
    delete updatedFilters[id];
    setFilters(updatedFilters);
  };

  const isFilterActive = (id: string) => {
    return !!filters[id];
  };

  const createSortHandler = (property: string) => {
    if (onRequestSort) onRequestSort(property);
  };

  const open = Boolean(anchorEl);
  const popperId = open ? "filter-popper" : undefined;

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.textAlign ?? "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={false}
            sx={{
              width: headCell?.minWidth,
              minWidth: headCell?.minWidth,
              maxWidth: headCell.maxWidth,
            }}
          >
            {headCell?.sortable ? (
              <Box display="flex" alignItems="center">
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={() => createSortHandler(headCell.id)}
                  sx={{ flexGrow: 1 }}
                >
                  <Typography
                    sx={{
                      ml: headCell?.ml,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    {headCell.label}
                  </Typography>
                  {orderBy === headCell.id && (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  )}
                </TableSortLabel>

                {!["id", "actions"].includes(headCell.id) && (
                  <>
                    <IconButton
                      aria-describedby={popperId}
                      onClick={(event) => handleClick(event, headCell.id)}
                    >
                      {isFilterActive(headCell.id) ? (
                        <FilterAltIcon />
                      ) : (
                        <FilterAltOffIcon />
                      )}
                    </IconButton>
                    {isFilterActive(headCell.id) && (
                      <IconButton
                        onClick={() => handleClearFilter(headCell.id)}
                        size="small"
                      >
                        <FilterAltOffIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Typography
                sx={{ ml: headCell?.ml, fontSize: "0.875rem", lineHeight: "1.5rem", letterSpacing: "0.01071em" }}
              >
                {headCell.label}
              </Typography>
            )}
          </TableCell>
        ))}
      </TableRow>

      <Popper id={popperId} open={open} anchorEl={anchorEl}>
        <Box
          bgcolor="#fff"
          p={1}
          display="flex"
          alignItems="center"
          border={1}
          borderRadius={1}
        >
          <Select
            label="Operator"
            value={tempFilterType}
            onChange={(e) => setTempFilterType(e.target.value)}
            size="small"
          >
            {[
              "status",
              "organization",
              "firstName",
              "lastName",
              "email",
              "city",
              "state",
            ].includes(tempFilterId) &&
              StringFilterOperators.map((item, index) => (
                <MenuItem key={index} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            {["ministryCategory", "requestStatus"].includes( 
              tempFilterId!
            ) &&
               StringFilterOperators.map((item, index) => (
                 <MenuItem key={index} value={item.value}>
                   {item.label}
                 </MenuItem>
                ))}
            {["requestTime", "shippedTime"].includes(tempFilterId) &&
              DateFilterOperators.map((item, index) => (
                <MenuItem key={index} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            {["redFlag", "yellowFlag"].includes(tempFilterId) &&
              BooleanFilterOperators.map((item, index) => (
                <MenuItem key={index} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
          </Select>

          {[
            "status",
            "organization",
            "firstName",
            "lastName",
            "email",
            "city",
            "state",
          ].includes(tempFilterId) && (
            <TextField
              label="Value"
              size="small"
              sx={{ mx: 1 }}
              value={tempFilterText as string}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setTempFilterText(event.target.value)
              }
            />
          )}

          {["ministryCategory", "requestStatus"].includes(
            tempFilterId!
          ) && (
            <TextField
              label="Value"
              sx={{ mx: 1 }}
              value={tempFilterText}
              onChange={(event) =>
                setTempFilterText(event.target.value)
              }
            />
           )}
          {["requestTime", "shippedTime"].includes(tempFilterId) && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                sx={{ width: 150, mx: 1 }}
                value={tempFilterText as Date | null}
                onChange={(value) => setTempFilterText(value ?? "")}
              />
            </LocalizationProvider>
          )}

          {["redFlag", "yellowFlag"].includes(tempFilterId) && (
            <Checkbox
              checked={Boolean(tempFilterText)}
              onChange={(event) => setTempFilterText(event.target.checked)}
              sx={{ mx: 1 }}
            />
          )}

          <Button
            variant="outlined"
            disabled={tempFilterText === ""}
            onClick={handleApplyFilter}
          >
            Apply
          </Button>
        </Box>
      </Popper>
    </TableHead>
  );
}
